import { useState } from "react";
import {
  Box as MuiBox,
  TextField as MuiTextField,
  CircularProgress,
  IconButton,
  InputAdornment,
  Alert,
  Stack
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import styled from "@emotion/styled";
import axios from "axios";
import CommonPageLayout from "../../../components/commonPageLayout/commonPageLayout";
import { CustomButton } from "../../../components/customTable/table.styled";

const Box = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 32px;
`;

const TextField = styled(MuiTextField)`
  max-width: 600px;
  label {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 0.15px;
  }

  input {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.15px;
  }
`;

function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const state: any = {
    token: localStorage?.getItem("businessAccessToken")
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }

    setChangingPassword(true);
    try {
      const config = {
        method: "POST",
        url: `${process.env.REACT_APP_API_BASE_URL}/users/businessportal/changepassword`,
        headers: {
          Authorization: "Bearer " + state?.token,
          "Content-Type": "application/json"
        },
        data: {
          CurrentPassword: currentPassword,
          NewPassword: newPassword
        }
      };

      await axios.request(config);
      setPasswordSuccess("Password changed successfully.");
      setPasswordError(null);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error changing password:", error);
      setPasswordError(
        error?.response?.data?.errors?.join(", ") || "Failed to change password"
      );
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <CommonPageLayout
      heading="Account"
      subHeading="Change Password"
      docs={null}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          {passwordError && (
            <Stack sx={{ width: "100%" }} spacing={2}>
              <Alert severity="error">{passwordError}</Alert>
            </Stack>
          )}
          {passwordSuccess && (
            <Stack sx={{ width: "100%" }} spacing={2}>
              <Alert severity="success">{passwordSuccess}</Alert>
            </Stack>
          )}

          <TextField
            id="current-password-input"
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}
            variant="outlined"
            autoComplete="new-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth={true}
            size="medium"
            disabled={changingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle current password visibility"
                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            id="new-password-input"
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            variant="outlined"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth={true}
            size="medium"
            disabled={changingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            id="confirm-password-input"
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            variant="outlined"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth={true}
            size="medium"
            disabled={changingPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
        <Box>
          <div>
          <CustomButton
            variant="contained"
            color="secondary"
            className={
              changingPassword ? "blue_button custom_disabled" : "blue_button"
            }
            onClick={handleChangePassword}
            disabled={changingPassword}
          >
            {changingPassword ? (
              <CircularProgress size={25} style={{ color: "inherit" }} />
            ) : (
                "Change Password"
              )}
            </CustomButton>
          </div>
        </Box>
      </div>
    </CommonPageLayout>
  );
}

export default ChangePasswordPage;
